/* global document, Event, HTMLInputElement, HTMLSelectElement */

import { getInputValue, getMultiSelectValues, getRadioGroupValue } from '../../shared/dom.util';
import {
    TRACE_SHEET_FILTER_PAYLOAD_VERSION,
    type TraceSheetFilterFieldValue,
    type TraceSheetFilterPersistedV1,
} from '../models/trace-sheet-filter-persisted.model';
import { FILTER_RECORD_TYPE_ELEMENT_ID } from '../record-type/record-type-field';
import type {
    ReportFilterFieldSpec,
    ReportingTargetFilterDefinition,
} from '../reporting-target/reporting-target-filter.types';

function allFieldSpecs(
    definition: ReportingTargetFilterDefinition
): readonly ReportFilterFieldSpec[] {
    return [...definition.commonFilterFields, ...definition.targetFilterFields];
}

/**
 * Captures current task-pane filter values for a reporting target (after DOM has been rendered for that target).
 */
export function buildTraceSheetFilterPersistedFromDom(params: {
    targetId: string;
    definition: ReportingTargetFilterDefinition;
    fetchFingerprint: string | null;
}): TraceSheetFilterPersistedV1 {
    const { targetId, definition, fetchFingerprint } = params;
    const fieldValues: Record<string, TraceSheetFilterFieldValue> = {};

    for (const spec of allFieldSpecs(definition)) {
        if (spec.kind === 'multiselect') {
            fieldValues[spec.elementId] = getMultiSelectValues(spec.elementId);
        } else if (spec.kind === 'radio') {
            fieldValues[spec.elementId] = getRadioGroupValue(spec.elementId);
        } else {
            fieldValues[spec.elementId] = getInputValue(spec.elementId);
        }
    }

    const recordType = definition.recordTypeFilter
        ? getInputValue(FILTER_RECORD_TYPE_ELEMENT_ID)
        : null;

    return {
        version: TRACE_SHEET_FILTER_PAYLOAD_VERSION,
        targetId,
        recordType,
        fieldValues,
        fetchFingerprint,
        savedAt: new Date().toISOString(),
    };
}

function applyMultiselectValues(elementId: string, values: readonly string[]): void {
    const root = document.getElementById(elementId);
    if (!root?.classList.contains('report-multiselect')) {
        return;
    }
    const selected = new Set(values);
    const checkboxes = root.querySelectorAll<HTMLInputElement>(
        '.report-multiselect__option input[type="checkbox"]'
    );
    checkboxes.forEach((cb) => {
        cb.checked = selected.has(cb.value.trim());
        cb.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

function applyRadioGroupValue(elementId: string, value: string): void {
    const root = document.getElementById(elementId);
    if (!root) {
        return;
    }
    const radios = root.querySelectorAll<HTMLInputElement>(
        `input[type="radio"][name="${elementId}"]`
    );
    radios.forEach((radio) => {
        radio.checked = radio.value.trim() === value;
    });
}

function applyScalarField(spec: ReportFilterFieldSpec, value: string): void {
    const el = document.getElementById(spec.elementId) as
        | HTMLInputElement
        | HTMLSelectElement
        | null;
    if (!el) {
        return;
    }
    el.value = value;
    if (spec.kind === 'date') {
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

/**
 * Writes a persisted snapshot into the current task pane DOM for the given definition.
 * Call after panels have been rendered for {@link snapshot.targetId}.
 */
export function applyTraceSheetFilterPersistedToDom(params: {
    snapshot: TraceSheetFilterPersistedV1;
    definition: ReportingTargetFilterDefinition;
}): void {
    const { snapshot, definition } = params;

    if (definition.recordTypeFilter && snapshot.recordType !== null) {
        const sel = document.getElementById(
            FILTER_RECORD_TYPE_ELEMENT_ID
        ) as HTMLSelectElement | null;
        const allowed = new Set(definition.recordTypeFilter.options.map((o) => o.value));
        if (sel && allowed.has(snapshot.recordType)) {
            sel.value = snapshot.recordType;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    for (const spec of allFieldSpecs(definition)) {
        const raw = snapshot.fieldValues[spec.elementId];
        if (raw === undefined) {
            continue;
        }
        if (spec.kind === 'multiselect') {
            const values = Array.isArray(raw) ? raw : [];
            applyMultiselectValues(spec.elementId, values);
        } else if (spec.kind === 'radio' && typeof raw === 'string') {
            applyRadioGroupValue(spec.elementId, raw);
        } else if (typeof raw === 'string') {
            applyScalarField(spec, raw);
        }
    }
}
